/**
 * Store-backed SelectionController — the same surface the legacy
 * `useSelection` exposed, implemented over WorkspaceStore slices.
 *
 * Selection, the drill-down back-stack, blast radius, and the active
 * process now LIVE in the store (shared with every workbench primitive);
 * this hook only adapts them to the controller contract App/ViewerShell
 * already consume, and registers the board's viewport pair as the store's
 * `ViewportSeam` so drill-downs snapshot the camera and `goBack` restores
 * it. The legacy hook's synchronous mirror refs are unnecessary here —
 * `store.getState()` is always same-frame consistent.
 */
import { useCallback, useEffect, useMemo } from "react";
import type { StableId } from "@archlang/scene/arch-model";
import type { Viewport } from "@archlang/scene/board";
import {
  useBlastRadius,
  useNavHistory,
  useWorkspaceSelection,
  type WorkspaceStore,
} from "../workbench/WorkspaceStore.js";

export interface Selection {
  moduleId: StableId | null;
  edgeId: string | null;
  activeProcessId?: StableId | null;
}
export type HistoryEntry = Selection & { viewport: Viewport | null };

export interface SelectionDeps {
  /** Current board viewport, snapshotted onto the back-stack. */
  readonly getViewport: () => Viewport | null;
  /** Restore a viewport when stepping back to a drill-down origin. */
  readonly restoreViewport: (vp: Viewport) => void;
}

export interface SelectionController {
  readonly selectedModuleId: StableId | null;
  readonly selectedEdgeId: string | null;
  readonly selectedInterfaceKey: string | null;
  readonly blastRadius: readonly StableId[];
  readonly activeProcessId: StableId | null;

  readonly setSelectedModuleId: (id: StableId | null) => void;
  readonly setSelectedInterfaceKey: (key: string | null) => void;
  readonly setActiveProcessId: (id: StableId | null) => void;
  readonly setBlastRadius: (ids: readonly StableId[]) => void;

  /** Synchronous read of the live selection (module/edge/process). */
  readonly currentSelection: () => Selection;
  readonly applySelection: (next: Selection, source: "board" | "sidebar") => void;
  readonly goBack: () => void;
  readonly canGoBack: boolean;
  /** Drill-down back-stack, for the breadcrumb / back button. */
  readonly navHistory: readonly HistoryEntry[];
}

export function useStoreSelection(
  store: WorkspaceStore,
  deps: SelectionDeps,
): SelectionController {
  const { getViewport, restoreViewport } = deps;

  useEffect(
    () => store.setViewportSeam({ getViewport, restoreViewport }),
    [store, getViewport, restoreViewport],
  );

  const selection = useWorkspaceSelection(store);
  const blastRadius = useBlastRadius(store);
  const storeHistory = useNavHistory(store);

  // Legacy HistoryEntry shape (flattened selection + viewport). Only the
  // stack depth is rendered today, but the controller contract stays whole.
  const navHistory = useMemo<readonly HistoryEntry[]>(
    () =>
      storeHistory.map((e) => ({
        moduleId: e.selection.moduleId as StableId | null,
        edgeId: e.selection.edgeId,
        activeProcessId: e.selection.activeProcessId as StableId | null,
        viewport: e.viewport,
      })),
    [storeHistory],
  );

  const currentSelection = useCallback((): Selection => {
    const s = store.getState().selection;
    return {
      moduleId: s.moduleId as StableId | null,
      edgeId: s.edgeId,
      activeProcessId: s.activeProcessId as StableId | null,
    };
  }, [store]);

  const applySelection = useCallback(
    (next: Selection, source: "board" | "sidebar") => {
      store.applySelection(
        {
          moduleId: next.moduleId,
          edgeId: next.edgeId,
          // undefined keeps the current process; null clears it explicitly.
          activeProcessId: next.activeProcessId,
        },
        source,
      );
    },
    [store],
  );

  return {
    selectedModuleId: selection.moduleId as StableId | null,
    selectedEdgeId: selection.edgeId,
    selectedInterfaceKey: selection.interfaceKey,
    blastRadius: blastRadius as readonly StableId[],
    activeProcessId: selection.activeProcessId as StableId | null,
    setSelectedModuleId: useCallback(
      (id: StableId | null) => store.select({ moduleId: id }),
      [store],
    ),
    setSelectedInterfaceKey: useCallback(
      (key: string | null) => store.select({ interfaceKey: key }),
      [store],
    ),
    setActiveProcessId: useCallback(
      (id: StableId | null) => store.select({ activeProcessId: id }),
      [store],
    ),
    setBlastRadius: useCallback(
      (ids: readonly StableId[]) => store.setBlastRadius(ids),
      [store],
    ),
    currentSelection,
    applySelection,
    goBack: useCallback(() => store.goBack(), [store]),
    canGoBack: storeHistory.length > 0,
    navHistory,
  };
}
